import { makeAutoObservable } from 'mobx';
import { makePersistable } from 'mobx-persist-store';
import { z } from 'zod';

import {
  ACTIVE_WIDGET_IDS,
  DEFAULT_WIDGET_ENTRIES,
  defaultSingleton,
} from './widget-defaults';
import {
  SINGLETON_WIDGET_IDS,
  WidgetEntrySchema,
  widgetEntryKey,
  type IslandEntry,
  type ScheduleFilterMode,
  type SingletonEntry,
  type SingletonWidgetId,
  type WidgetEntry,
} from './widgets.types';

/** Old persisted shape (entries keyed by `id`), migrated forward so panels aren't reset. */
const LegacyEntrySchema = z.object({
  id: z.enum(SINGLETON_WIDGET_IDS),
  visible: z.boolean(),
  collapsed: z.boolean(),
  order: z.number(),
});

/** Parse one persisted (untrusted) entry into a valid WidgetEntry, or drop it. */
function migrateEntry(raw: unknown): WidgetEntry | null {
  const current = WidgetEntrySchema.safeParse(raw);
  if (current.success) return current.data;

  const legacy = LegacyEntrySchema.safeParse(raw);
  if (legacy.success) {
    return {
      kind: 'singleton',
      type: legacy.data.id,
      visible: legacy.data.visible,
      collapsed: legacy.data.collapsed,
      order: legacy.data.order,
    };
  }

  return null;
}

function isSingleton(entry: WidgetEntry): entry is SingletonEntry {
  return entry.kind === 'singleton';
}

function isIsland(entry: WidgetEntry): entry is IslandEntry {
  return entry.kind !== 'singleton';
}

function parsePersistedEntries(raw: unknown): WidgetEntry[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .map(migrateEntry)
    .filter((entry): entry is WidgetEntry => entry !== null);
}

/** One entry per active singleton type: keep persisted, prune removed/hidden, re-add missing. */
function reconcileSingletons(persisted: SingletonEntry[]): SingletonEntry[] {
  const byType = new Map<SingletonWidgetId, SingletonEntry>();
  for (const entry of persisted) {
    if (ACTIVE_WIDGET_IDS.includes(entry.type) && !byType.has(entry.type)) {
      byType.set(entry.type, entry);
    }
  }
  for (const id of ACTIVE_WIDGET_IDS) {
    if (!byType.has(id)) byType.set(id, defaultSingleton(id));
  }
  return [...byType.values()];
}

const STORAGE_NAME = 'widgetsConfigStore';

export class WidgetsConfigStore {
  entries: WidgetEntry[] = DEFAULT_WIDGET_ENTRIES;
  scheduleFilterMode: ScheduleFilterMode = 'all';

  constructor() {
    makeAutoObservable(this);
    makePersistable(this, {
      name: STORAGE_NAME,
      properties: ['entries', 'scheduleFilterMode'],
      storage: window.localStorage,
    }).then(() => {
      this.reconcile();
    });
  }

  setScheduleFilterMode(mode: ScheduleFilterMode) {
    this.scheduleFilterMode = mode;
  }

  setVisible(key: string, visible: boolean) {
    this.entries = this.entries.map((entry) =>
      widgetEntryKey(entry) === key ? { ...entry, visible } : entry
    );
  }

  isCollapsed(key: string): boolean {
    return (
      this.entries.find((entry) => widgetEntryKey(entry) === key)?.collapsed ??
      false
    );
  }

  setCollapsed(key: string, collapsed: boolean) {
    this.entries = this.entries.map((entry) =>
      widgetEntryKey(entry) === key ? { ...entry, collapsed } : entry
    );
  }

  setOrder(orderedKeys: string[]) {
    const orderByKey = new Map(orderedKeys.map((key, index) => [key, index]));
    this.entries = this.entries.map((entry) => {
      const nextOrder = orderByKey.get(widgetEntryKey(entry));
      return nextOrder === undefined ? entry : { ...entry, order: nextOrder };
    });
  }

  /** Pin a routine as a new island, or reveal it if already pinned. */
  addPinnedRoutine(routineId: number) {
    const existing = this.entries.find(
      (entry) =>
        entry.kind === 'pinned-routine' && entry.routineId === routineId
    );
    if (existing) {
      this.setVisible(widgetEntryKey(existing), true);
      return;
    }
    const maxOrder = this.entries.reduce(
      (max, entry) => Math.max(max, entry.order),
      -1
    );
    this.entries = [
      ...this.entries,
      {
        kind: 'pinned-routine',
        routineId,
        visible: true,
        collapsed: false,
        order: maxOrder + 1,
      },
    ];
  }

  removeInstance(key: string) {
    this.entries = this.entries.filter(
      (entry) => entry.kind === 'singleton' || widgetEntryKey(entry) !== key
    );
  }

  reset() {
    this.entries = DEFAULT_WIDGET_ENTRIES.map((entry) => ({ ...entry }));
  }

  get orderedVisibleEntries(): WidgetEntry[] {
    return [...this.entries]
      .filter((entry) => entry.visible)
      .sort((a, b) => a.order - b.order);
  }

  private reconcile(): void {
    const migrated = parsePersistedEntries(this.entries);
    const singletons = reconcileSingletons(migrated.filter(isSingleton));
    // drop duplicate islands (same routineId) from corrupted persisted state
    const keys = new Set<string>();
    const islands = migrated.filter(isIsland).filter((island) => {
      const key = widgetEntryKey(island);
      if (keys.has(key)) return false;
      keys.add(key);
      return true;
    });
    this.entries = [...singletons, ...islands]
      .sort((a, b) => a.order - b.order)
      .map((entry, order) => ({ ...entry, order }));
  }
}

export const widgetsConfigStore = new WidgetsConfigStore();
